/**
 * Table cells that make a list breathe: one main line, one small grey line, and quiet metadata.
 *
 *   <PrimaryCell icon="lucide:scan-text" title="Ultraboost Light running shoes" subtitle="example-store.com/collections/…" subtitleTitle={url} />
 *   <StatusText status={log.status} />                      icon + word in the status colour (no pill)
 *   <RelativeTime value={log.started_at} />                 "12 min ago", exact date and time on hover
 *   <MonoChip value={uuid.slice(0, 8)} title={uuid} />      small mono chip for ids
 *   <ListCard icon=… title=… subtitle=… status=… meta=… time=… />   the phone version of a row (DataTable mobileCard)
 *
 * Long values never wrap: both lines truncate with an ellipsis and carry the full value in `title`. The cell
 * truncates inside the width its column gets, so use it in a DataTable with `layout="fixed"` (or give it a max width).
 */
import { ReactNode } from "react";
import { Icon } from "@iconify/react";
import { getStatus, type StatusKey } from "@/lib/status";
import { EMPTY, clampText, formatDateTime, formatUrlForDisplay } from "@/lib/format";

/** A 36px rounded tile holding a small grey icon, like the leading tile of a list row. `tone` swaps the grey
 * outline for a soft tint (lib/toolTone), e.g. one colour per tool. */
export const IconTile = ({ icon, tone, className = "" }: { icon: ReactNode; tone?: string; className?: string }) => (
  <span
    aria-hidden="true"
    className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-control ${
      tone ?? "border border-line bg-surface text-fg-secondary shadow-[0_1px_1px_rgb(0_0_0/0.03)]"
    } ${className}`}
  >
    {typeof icon === "string" ? <Icon icon={icon} className="text-lg" /> : icon}
  </span>
);

interface PrimaryCellProps {
  /** Iconify name or a node; rendered in an IconTile. */
  icon?: ReactNode;
  /** Soft tint for the icon tile (lib/toolTone). */
  iconTone?: string;
  title: ReactNode;
  /** Full text of the title for the hover tooltip (defaults to the title when it is a string). */
  titleText?: string;
  subtitle?: ReactNode;
  /** Full text of the second line for the hover tooltip (defaults to the subtitle when it is a string). */
  subtitleText?: string;
  /** Something small after the title on the same line (a "+2" count, a chip). Never truncated. */
  titleAddon?: ReactNode;
  className?: string;
}

/** List cells never run long: titles and second lines are capped in characters (full text on hover) and in width. */
const TITLE_MAX = 60;
const SUBTITLE_MAX = 80;

export const PrimaryCell = ({ icon, iconTone, title, titleText, subtitle, subtitleText, titleAddon, className = "" }: PrimaryCellProps) => {
  const titleFull = titleText ?? (typeof title === "string" ? title : undefined);
  const subtitleFull = subtitleText ?? (typeof subtitle === "string" ? subtitle : undefined);
  return (
    <div className={`flex min-w-0 items-center gap-3 ${className}`}>
      {icon ? <IconTile icon={icon} tone={iconTone} /> : null}
      <div className="min-w-0 max-w-[34rem] flex-1 leading-5">
        <div className="flex min-w-0 items-center gap-1.5">
          <span dir="auto" className="truncate text-sm font-medium text-fg" title={titleFull}>
            {typeof title === "string" ? clampText(title, TITLE_MAX) : title}
          </span>
          {titleAddon ? <span className="shrink-0">{titleAddon}</span> : null}
        </div>
        {subtitle ? (
          <p dir="auto" className="truncate text-13 text-fg-muted" title={subtitleFull}>
            {typeof subtitle === "string" ? clampText(subtitle, SUBTITLE_MAX) : subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
};

const STATUS_ICON: Record<StatusKey, { icon: string; color: string }> = {
  completed: { icon: "lucide:circle-check", color: "text-success-text" },
  running: { icon: "lucide:loader-circle", color: "text-primary-text" },
  queued: { icon: "lucide:clock", color: "text-fg-secondary" },
  failed: { icon: "lucide:circle-x", color: "text-danger-text" },
  cancelled: { icon: "lucide:circle-slash", color: "text-fg-muted" },
  partial: { icon: "lucide:circle-alert", color: "text-warning-text" },
};

/** Status as an icon and a word in the status colour. Maps any raw API status through lib/status. */
export const StatusText = ({ status, label, className = "" }: { status: string | null | undefined; label?: string; className?: string }) => {
  const s = getStatus(status);
  const look = STATUS_ICON[s.key] ?? STATUS_ICON.queued;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-sm ${look.color} ${className}`}>
      <Icon icon={look.icon} className={`shrink-0 text-base ${s.key === "running" ? "motion-safe:animate-spin" : ""}`} aria-hidden="true" />
      {label ?? s.label}
    </span>
  );
};

/** "12 min ago" with the exact date and time on hover. */
export const RelativeTime = ({ value, className = "" }: { value: string | null | undefined; className?: string }) =>
  value ? (
    <time dateTime={value} title={formatDateTime(value, "datetime")} className={`whitespace-nowrap text-sm text-fg-secondary ${className}`}>
      {formatDateTime(value, "relative")}
    </time>
  ) : (
    <span className="text-fg-muted">{EMPTY}</span>
  );

/** A small mono chip for ids and keys. */
export const MonoChip = ({ value, title, icon, className = "" }: { value: ReactNode; title?: string; icon?: string; className?: string }) => (
  <span
    title={title}
    className={`inline-flex max-w-full items-center gap-1 rounded-md border border-line bg-surface px-1.5 py-0.5 font-mono text-xs text-fg-secondary ${className}`}
  >
    {icon ? <Icon icon={icon} className="shrink-0 text-xs text-fg-muted" aria-hidden="true" /> : null}
    <span className="truncate">{value}</span>
  </span>
);

interface ListCardProps {
  icon?: ReactNode;
  iconTone?: string;
  title: ReactNode;
  titleText?: string;
  subtitle?: ReactNode;
  subtitleText?: string;
  titleAddon?: ReactNode;
  status?: ReactNode;
  /** Small grey facts after the status ("4 credits"). */
  meta?: ReactNode;
  time?: string | null;
}

/** The phone layout of a list row: the same two-line cell, then status, facts and time on one quiet line. */
export const ListCard = ({ icon, iconTone, title, titleText, subtitle, subtitleText, titleAddon, status, meta, time }: ListCardProps) => (
  <div className="min-w-0">
    <PrimaryCell icon={icon} iconTone={iconTone} title={title} titleText={titleText} subtitle={subtitle} subtitleText={subtitleText} titleAddon={titleAddon} />
    {(status || meta || time) && (
      <div className={`mt-2 flex min-w-0 items-center justify-between gap-3 ${icon ? "pl-12" : ""}`}>
        <span className="flex min-w-0 items-center gap-2">
          {status}
          {meta ? <span className="truncate text-13 text-fg-muted">{meta}</span> : null}
        </span>
        {time !== undefined ? <RelativeTime value={time} className="shrink-0 text-13 text-fg-muted" /> : null}
      </div>
    )}
  </div>
);

/**
 * A URL on one line that never widens its column: no protocol or "www.", the host in full, the path cut in the
 * middle by the space available (its last characters stay visible), the full URL on hover.
 *   <UrlText url="https://www.google.com/maps/place/data=!4m2!3m1!1s0x88:0x5d9c722d77edf443" />
 *     -> google.com/maps/place/data=!4m2…edf443   (as much of the path as fits)
 * `query={false}` shows domain and path only.
 */
export const UrlText = ({ url, query = true, className = "" }: { url: string; query?: boolean; className?: string }) => {
  const text = formatUrlForDisplay(url, 100000, { query });
  const slash = text.search(/[/?#]/);
  const host = slash === -1 ? text : text.slice(0, slash);
  const rest = slash === -1 ? "" : text.slice(slash);
  const tail = rest.length > 14 ? rest.slice(-6) : "";
  const head = tail ? rest.slice(0, -6) : rest;
  return (
    <span title={url} className={`inline-flex min-w-0 max-w-full align-bottom ${className}`}>
      <span className={`${rest ? "max-w-[70%] shrink-0" : "min-w-0"} truncate`}>{host}</span>
      {head ? <span className="min-w-0 truncate">{head}</span> : null}
      {tail ? <span className="shrink-0">{tail}</span> : null}
    </span>
  );
};
